'use client';

import { faBug } from '@fortawesome/free-solid-svg-icons';
import { ActionIcon, Box } from '@mantine/core';
import { type ReactNode, useCallback, useEffect } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { logException, logWarning } from '../../common/util/logging/writeLog';
import { useAppInsights } from '../AppInsightsWrapper/AppInsightsWrapper';
import { ContentWithDefault } from '../contentWithDefault/ContentWithDefault';
import { Icon } from '../Spinner/Icon';
import styles from './CommonError.module.scss';

export interface ErrorProps {
	tooltipContent?: ReactNode;
	error: Error & { digest?: string };
	reset?: () => Promise<unknown>;
}

function DefaultErrorContent(): ReactNode {
	return <>Something went wrong</>;
}

export function CommonError(props: ErrorProps): ReactNode {
	const appInsights = useAppInsights();

	const error = props.error;
	const reset = props.reset;

	useEffect(() => {
		if (error) {
			// Log the error to an error reporting service
			logException(appInsights, error);
		}
	}, [error, appInsights]);

	const attemptReset = useCallback(() => {
		if (!reset) {
			return;
		}

		// Attempt to recover by trying to re-render the segment
		logWarning(appInsights, 'Attempting to reload component');
		void reset();
	}, [appInsights, reset]);

	const renderTooltip = (tooltipProps: object) => {
		return (
			<Popover className={styles['errorTooltip']} {...tooltipProps}>
				<Popover.Header>Error</Popover.Header>
				<Popover.Body>
					<ContentWithDefault content={props.tooltipContent} defaultContent={<DefaultErrorContent />} />
				</Popover.Body>
			</Popover>
		);
	};

	// TODO: add tooltip here

	return (
		<Box className={styles['main']}>
			<OverlayTrigger placement="bottom" flip={true} overlay={renderTooltip} delay={50}>
				<ActionIcon onClick={attemptReset} color="#990000">
					<Icon className="errorIcon" icon={faBug} aria-label="Error" />
				</ActionIcon>
			</OverlayTrigger>
		</Box>
	);
}
